import React, { useEffect, useState } from "react";
import { AiOutlineClose, AiOutlinePlus, AiOutlineSearch } from "react-icons/ai";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import { FaUsers } from "react-icons/fa";
import { de } from "@faker-js/faker";
import { showApiErrors } from "../../utils/errorHandler";

const CreateGroupExecutive = ({groupId, fetchGroupExecutives,setShowAddExecutiveModal, setShowCreatePositionModal, fetchExecutivePositions,executivePositions}) => {
    const { requestWithAuth } = useAuth();
    const [groupMembers, setGroupMembers] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedMember, setSelectedMember] = useState(null);

    useEffect(()=>{
        // Only fetch if search has at least 3 characters
        if (debouncedSearch.length >= 3) {
            fetchGroupMembers();
        } else {
            // Clear members list if search is less than 3 characters and no member is selected
            if (!selectedMember) {
                setGroupMembers([]);
            }
        }
    }, [debouncedSearch]);

    const fetchGroupMembers = async () => {
        try {
            const response = await requestWithAuth(
                `/groups/group-members/${groupId}?search=${debouncedSearch}&paginated=false`,
                'GET'
            );
            
            setGroupMembers(response?.data);
        } catch (error) {
            console.error("Failed to fetch group members:", error);
            toast.error("Failed to load group members");
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    // Form states for adding executive
    const [executiveForm, setExecutiveForm] = useState({
        member: '',
        position_id: '',
        username: '',
        password: '',
        is_current: true,
        create_user_account: false
    });

    // Handle member selection
    const handleMemberSelect = (member) => {
        setSelectedMember(member);
        setExecutiveForm((prev) => ({ ...prev, member: member.member_id }));
    };

    // Get filtered members to display
    const getDisplayMembers = () => {
        if (selectedMember) {
            // If a member is selected, only show that member
            return [selectedMember];
        }
        
        // If search is less than 3 characters, don't show any members
        if (search.length < 3) {
            return [];
        }
        
        // Otherwise show all fetched members
        return groupMembers;
    };

    // Executive Actions
    const handleAddExecutive = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                member: executiveForm.member,
                position: executiveForm.position_id,
                is_current: executiveForm.is_current
            };

            if (executiveForm.create_user_account) {
                payload.username = executiveForm.username;
                payload.password = executiveForm.password;
            }

            await requestWithAuth(`/groups/group-executives/${groupId}/`, 'POST', payload);
            
            toast.success("Executive added successfully!");
            setShowAddExecutiveModal(false);
            setExecutiveForm({
                member: '',
                position_id: '',
                username: '',
                password: '',
                is_current: true,
                create_user_account: false
            });
            setSelectedMember(null);
            setSearch('');
            setGroupMembers([]);
            fetchGroupExecutives();
            
        } catch (error) {
            showApiErrors(error);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        
        // If search is less than 3 characters, clear selected member
        if (value.length < 3) {
            setSelectedMember(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Add Group Executive
                        </h3>
                        <button
                            onClick={() => setShowAddExecutiveModal(false)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <AiOutlineClose className="text-xl text-gray-600" />
                        </button>
                    </div>

                    <form onSubmit={handleAddExecutive} className="space-y-4">
                        {/* Member Selection */}
                        {/* Search */}
                        <div className="relative mb-4">
                            <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search group members... (min 3 characters)"
                                value={search}
                                onChange={(e) => handleSearchChange(e)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Available Members List */}
                        <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                            {getDisplayMembers()?.length > 0 ? (
                                getDisplayMembers()?.map((member) => (
                                    <label
                                        key={member.id}
                                        onClick={() => handleMemberSelect(member)}
                                        className={`flex items-center p-3 rounded-lg border ${executiveForm?.member == member.member_id ? "border-blue-500 bg-blue-50":"border-gray-300" } hover:bg-gray-50 cursor-pointer`}
                                    >
                                        <div className="flex items-center gap-3 flex-grow">
                                            <div>
                                                <p className={`font-medium ${executiveForm?.member == member.id ? "text-blue-900":"text-gray-900" }`}>{member.full_name}</p>
                                                <p className={`text-sm ${executiveForm?.member == member.id ? "text-blue-500":"text-gray-500" }`}>{member.email}</p>
                                            </div>
                                        </div>
                                    </label>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                                    {search?.length > 0 && search.length < 3 ? (
                                        <p className="text-gray-500">
                                            Please enter at least 3 characters to search for members
                                        </p>
                                    ) : search?.length >= 3 ? (
                                        <p className="text-gray-500">
                                            No members found matching your search
                                        </p>
                                    ) : (
                                        <p className="text-gray-500">
                                            Enter at least 3 characters to search for members
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Position Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Executive Position *
                            </label>
                            <div className="flex gap-2">
                                <select
                                    value={executiveForm.position_id}
                                    onChange={(e) =>
                                        setExecutiveForm((prev) => ({
                                            ...prev,
                                            position_id: e.target.value,
                                        }))
                                    }
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Choose position</option>
                                    {executivePositions?.map((position) => (
                                        <option key={position.id} value={position.id}>
                                            {position.position}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setShowCreatePositionModal(true)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    title="Create new position"
                                >
                                    <AiOutlinePlus />
                                </button>
                            </div>
                        </div>

                        {/* Current Executive Toggle */}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="is_current"
                                checked={executiveForm.is_current}
                                onChange={(e) =>
                                    setExecutiveForm((prev) => ({
                                        ...prev,
                                        is_current: e.target.checked,
                                    }))
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                                htmlFor="is_current"
                                className="text-sm font-medium text-gray-700"
                            >
                                Current Executive
                            </label>
                        </div>

                        {/* Create User Account Toggle */}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="create_user_account"
                                checked={executiveForm.create_user_account}
                                onChange={(e) =>
                                    setExecutiveForm((prev) => ({
                                        ...prev,
                                        create_user_account: e.target.checked,
                                    }))
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                                htmlFor="create_user_account"
                                className="text-sm font-medium text-gray-700"
                            >
                                Create user account for this executive
                            </label>
                        </div>

                        {/* Username and Password fields (conditional) */}
                        {executiveForm.create_user_account && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Username *
                                    </label>
                                    <input
                                        type="text"
                                        value={executiveForm.username}
                                        onChange={(e) =>
                                            setExecutiveForm((prev) => ({
                                                ...prev,
                                                username: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required={executiveForm.create_user_account}
                                        placeholder="Enter username"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password *
                                    </label>
                                    <input
                                        type="password"
                                        value={executiveForm.password}
                                        onChange={(e) =>
                                            setExecutiveForm((prev) => ({
                                                ...prev,
                                                password: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required={executiveForm.create_user_account}
                                        placeholder="Enter password"
                                    />
                                </div>
                            </>
                        )}

                        {/* Submit Button */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowAddExecutiveModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!executiveForm.member || !executiveForm.position_id}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                            >
                                Add Executive
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateGroupExecutive;